<template>
  <!-- With `track`, persistent while it holds changes: Esc or a click beside it would throw them away. -->
  <v-dialog v-model="dialog" :max-width="maxWidth" :persistent="persistent || changes.isDirty.value" scrollable>
    <template #activator="{ props }">
      <slot name="activator" v-bind="{ props }" />
    </template>

    <!-- `modal-dialog`, `modal-dialog-cancel` and `modal-dialog-submit` are the walkthrough's
         anchors, the same three `LewFormDialog` carries. They live here rather than in each caller
         because the window and its two buttons are this component's, not theirs. -->
    <v-card class="lew-modal-card" data-help="modal-dialog">
      <div class="lew-modal-card__header">
        <!-- The heading is the `title` and `subtitle` props, unless a caller draws its own — the
             billing window puts an icon beside its two lines. It passed a `#title` template before
             there was a slot to catch it, so its header rendered empty. -->
        <slot name="title">
          <v-card-title class="lew-modal-card__title">
            {{ title }}
          </v-card-title>
          <v-card-subtitle v-if="subtitle" class="lew-modal-card__subtitle">
            {{ subtitle }}
          </v-card-subtitle>
        </slot>
      </div>

      <v-divider />

      <v-card-text class="lew-modal-card__body">
        <slot />
      </v-card-text>

      <template v-if="!hideActions">
        <v-divider />
        <v-card-actions class="lew-modal-card__actions">
          <v-spacer />
          <slot name="actions" :submit="submit" :cancel="cancel">
            <v-btn variant="text" class="font-weight-medium" data-help="modal-dialog-cancel" @click="cancel">
              {{ $t("general.actions.cancel") }}
            </v-btn>
            <v-btn
              variant="elevated"
              color="primary"
              class="font-weight-medium"
              data-help="modal-dialog-submit"
              :disabled="disabledSubmit"
              @click="submit"
            >
              {{ $t("general.actions.submit") }}
            </v-btn>
          </slot>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue"
import { useDialogChanges } from "@/composables/useUnsavedChanges"

export default {
  name: "LewModalCard",
  props: {
    maxWidth: { type: Number, default: 800 },
    persistent: { type: Boolean, default: false },
    title: { type: String, default: "" },
    subtitle: { type: String, required: false },
    modelValue: { type: Boolean, default: false },
    form: { type: Object, default: null },
    hideActions: { type: Boolean, default: false },
    disabledSubmit: { type: Boolean, default: false },
    /**
     * What the dialog edits, for a dialog that edits something: while it differs from what the dialog
     * opened with, only the dialog's own buttons close it, and Abbrechen asks first.
     */
    track: { type: Function, default: null },
  },
  emits: ["update:modelValue", "submit:agree", "submit:disagree"],
  setup(props, { emit }) {
    const dialog = ref(props.modelValue)
    const validForm = ref(true)
    const changes = useDialogChanges({
      open: () => dialog.value && Boolean(props.track),
      state: () => props.track?.(),
      title: () => props.title,
    })

    watch(
      () => props.modelValue,
      newValue => {
        dialog.value = newValue
      }
    )
    watch(
      () => dialog.value,
      () => {
        emit("update:modelValue", dialog.value)
      }
    )

    async function submit() {
      if (props.form) {
        validForm.value = await props.form.validate()
      }
      if (validForm.value) {
        dialog.value = false
        emit("submit:agree")
      }
    }

    async function cancel() {
      if (props.track && !(await changes.mayClose())) return
      dialog.value = false
      emit("submit:disagree")
    }

    return { dialog, changes, submit, cancel }
  },
}
</script>

<style scoped>
.lew-modal-card {
  overflow: hidden;
}

/* Der Kopf ist ein eigenes div und damit kein v-card-Slot mit `flex: none` — ohne die
   Angabe wird er mitgestaucht, sobald der Inhalt hoch wird. Titel und Fussleiste stehen,
   nur der Rumpf scrollt, damit die Aktionen immer sichtbar bleiben. */
.lew-modal-card__header {
  flex: none;
  padding: 20px 24px 16px;
}

.lew-modal-card__title {
  font-size: 1.1rem !important;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0;
  line-height: 1.4;
}

/* Vuetify gibt `.v-card-subtitle` standardmaessig `white-space: nowrap` mit Ellipse.
   Unsere Untertitel sind ganze Saetze, die sonst mitten im Wort abgeschnitten werden. */
.lew-modal-card__subtitle {
  padding: 4px 0 0;
  font-size: 0.85rem;
  line-height: 1.45;
  white-space: normal;
  opacity: 0.65;
}

.lew-modal-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0;
}

.lew-modal-card__actions {
  padding: 12px 16px;
  gap: 8px;
}
</style>
